import * as THREE from 'three';
import type { CarSnapshot, CarSpec } from '../sim/types';

/**
 * Representación 3D estilizada del coche: carrocería, cabina, ruedas con
 * suspensión visible, luces de freno/marcha atrás/intermitentes y faros.
 */
export class CarVisual {
  readonly group = new THREE.Group();
  private wheelSteerGroups: THREE.Group[] = [];
  private wheelSpinMeshes: THREE.Mesh[] = [];
  private brakeMat: THREE.MeshStandardMaterial;
  private reverseMat: THREE.MeshStandardMaterial;
  private blinkLMat: THREE.MeshStandardMaterial;
  private blinkRMat: THREE.MeshStandardMaterial;
  private headMat: THREE.MeshStandardMaterial;
  private headlights: THREE.SpotLight[] = [];
  private spec: CarSpec;

  constructor(spec: CarSpec) {
    this.spec = spec;
    const d = spec.dims;
    const bodyColor = new THREE.Color(spec.color);

    const bodyMat = new THREE.MeshStandardMaterial({
      color: bodyColor,
      metalness: 0.35,
      roughness: 0.45,
    });
    const glassMat = new THREE.MeshStandardMaterial({
      color: 0x18222e,
      metalness: 0.1,
      roughness: 0.15,
    });
    const darkMat = new THREE.MeshStandardMaterial({ color: 0x22262b, roughness: 0.8 });

    // Carrocería
    const body = new THREE.Mesh(
      new THREE.BoxGeometry(d.widthM * 0.96, 0.52, d.lengthM * 0.96),
      bodyMat,
    );
    body.position.y = 0.02;
    body.castShadow = true;
    this.group.add(body);

    // Cabina
    const cabin = new THREE.Mesh(
      new THREE.BoxGeometry(d.widthM * 0.82, 0.46, d.lengthM * 0.45),
      glassMat,
    );
    cabin.position.set(0, 0.45, -d.lengthM * 0.06);
    cabin.castShadow = true;
    this.group.add(cabin);

    // Retrovisores exteriores
    for (const side of [-1, 1]) {
      const mirror = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.1, 0.18), darkMat);
      mirror.position.set(side * (d.widthM / 2 + 0.06), 0.52, d.lengthM * 0.14);
      this.group.add(mirror);
    }

    // Luces
    this.brakeMat = new THREE.MeshStandardMaterial({
      color: 0x550000,
      emissive: 0x000000,
    });
    this.reverseMat = new THREE.MeshStandardMaterial({ color: 0x666666, emissive: 0x000000 });
    this.blinkLMat = new THREE.MeshStandardMaterial({ color: 0x694400, emissive: 0x000000 });
    this.blinkRMat = new THREE.MeshStandardMaterial({ color: 0x694400, emissive: 0x000000 });
    this.headMat = new THREE.MeshStandardMaterial({ color: 0xd8d8c8, emissive: 0x000000 });

    const rearZ = -d.lengthM / 2 * 0.96 - 0.01;
    const frontZ = d.lengthM / 2 * 0.96 + 0.01;
    const lightY = 0.12;
    const addBox = (
      mat: THREE.MeshStandardMaterial,
      x: number,
      y: number,
      z: number,
      w = 0.3,
      h = 0.12,
    ): void => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, 0.06), mat);
      m.position.set(x, y, z);
      this.group.add(m);
    };
    const wHalf = d.widthM / 2 - 0.3;
    addBox(this.brakeMat, -wHalf, lightY, rearZ);
    addBox(this.brakeMat, wHalf, lightY, rearZ);
    addBox(this.reverseMat, 0.25, lightY, rearZ, 0.18, 0.1);
    addBox(this.blinkLMat, -(d.widthM / 2 - 0.08), lightY, rearZ, 0.1, 0.1);
    addBox(this.blinkRMat, d.widthM / 2 - 0.08, lightY, rearZ, 0.1, 0.1);
    addBox(this.blinkLMat, -(d.widthM / 2 - 0.08), lightY, frontZ, 0.1, 0.1);
    addBox(this.blinkRMat, d.widthM / 2 - 0.08, lightY, frontZ, 0.1, 0.1);
    addBox(this.headMat, -wHalf, lightY, frontZ);
    addBox(this.headMat, wHalf, lightY, frontZ);

    // Faros reales (spotlights)
    for (const side of [-1, 1]) {
      const spot = new THREE.SpotLight(0xfff4d6, 0, 45, 0.55, 0.5, 1.2);
      spot.position.set(side * wHalf, 0.25, frontZ);
      const target = new THREE.Object3D();
      target.position.set(side * wHalf * 0.6, -0.2, frontZ + 14);
      this.group.add(target);
      spot.target = target;
      this.group.add(spot);
      this.headlights.push(spot);
    }

    // Ruedas: grupo de dirección → malla que gira
    const tireMat = new THREE.MeshStandardMaterial({ color: 0x1c1c20, roughness: 0.9 });
    const rimMat = new THREE.MeshStandardMaterial({ color: 0xb9bcc2, metalness: 0.7, roughness: 0.3 });
    for (let i = 0; i < 4; i++) {
      const steer = new THREE.Group();
      const spin = new THREE.Group();
      const tire = new THREE.Mesh(
        new THREE.CylinderGeometry(d.wheelRadiusM, d.wheelRadiusM, 0.24, 22),
        tireMat,
      );
      tire.rotation.z = Math.PI / 2;
      tire.castShadow = true;
      const rim = new THREE.Mesh(
        new THREE.CylinderGeometry(d.wheelRadiusM * 0.55, d.wheelRadiusM * 0.55, 0.25, 12),
        rimMat,
      );
      rim.rotation.z = Math.PI / 2;
      spin.add(tire);
      spin.add(rim);
      steer.add(spin);
      this.group.add(steer);
      this.wheelSteerGroups.push(steer);
      this.wheelSpinMeshes.push(tire);
    }
  }

  /** Actualiza pose y estado luminoso. pos/quat vienen interpolados del bucle. */
  update(
    pos: THREE.Vector3,
    quat: THREE.Quaternion,
    snap: CarSnapshot | null,
    timeS: number,
  ): void {
    this.group.position.copy(pos);
    this.group.quaternion.copy(quat);
    if (!snap) return;

    for (let i = 0; i < 4; i++) {
      const w = snap.wheels[i];
      const steerGroup = this.wheelSteerGroups[i];
      if (!w || !steerGroup) continue;
      steerGroup.position.set(w.x, -0.15 - w.suspensionM, w.z);
      steerGroup.rotation.y = w.steerRad;
      const spin = steerGroup.children[0];
      if (spin) spin.rotation.x = w.spinRad;
    }

    const blinkOn = timeS % 0.8 < 0.4;
    this.brakeMat.emissive.setHex(snap.brakeLight ? 0xdd1111 : 0x000000);
    this.reverseMat.emissive.setHex(snap.gear === 'R' ? 0xdddddd : 0x000000);
    this.blinkLMat.emissive.setHex(snap.signalLeft && blinkOn ? 0xffa500 : 0x000000);
    this.blinkRMat.emissive.setHex(snap.signalRight && blinkOn ? 0xffa500 : 0x000000);
    this.headMat.emissive.setHex(snap.lights > 0 ? 0xfff2cc : 0x000000);
    const intensity = snap.lights === 0 ? 0 : snap.lights === 1 ? 55 : 130;
    for (const h of this.headlights) h.intensity = intensity;
  }

  get length(): number {
    return this.spec.dims.lengthM;
  }
}
